// src/utils/circlePulse.js
//
// The Friend Circle pulse, as plain functions: where a circle's current
// question is up to, and what anyone in it may do next. The screens use these
// to decide what to show; firestore.rules enforces the same clock and the same
// conditions (the rules suite checks the numbers match), so the screens can be
// wrong about what's allowed but never let anyone do something the rules
// don't.
//
// A circle document carries at most one question at a time:
//
//   pulse: {
//     id,          a fresh id per question, so no earlier answer counts
//     questionId,  one of src/data/circlePulseQuestions.js
//     startedAt,   server time it was asked
//     askedTo,     who was in the circle when it was asked
//     answeredBy,  who has answered - never what
//     revealed,    whether the answers are showing
//     revealedAt,  when they started showing
//   }
//
// and each answer is circles/{id}/pulse_answers/{uid}: { pulseId, choice,
// answeredAt }, where choice is the option's position.

import { getPulseQuestion, nextPulseQuestionId } from '../data/circlePulseQuestions';

const DAY = 24 * 60 * 60 * 1000;

/** With two or more answers in, a question may be revealed this long after it was asked. */
export const PULSE_REVEAL_AFTER_DAYS = 3;
/** A revealed question stays up at least this long before it can be replaced. */
export const PULSE_NEXT_AFTER_REVEAL_DAYS = 1;
/** After this long any question can be replaced, revealed or not. */
export const PULSE_LIFETIME_DAYS = 7;
/** Fewer answers than this are never revealed: one answer on its own just exposes one person. */
export const PULSE_MIN_ANSWERS_TO_REVEAL = 2;

/** Milliseconds from a Firestore Timestamp, a Date, a number or nothing. */
export function toMillis(value) {
  if (value == null) return null;
  if (typeof value === 'number') return value;
  if (typeof value.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value.seconds === 'number') return value.seconds * 1000;
  const parsed = new Date(value).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

const includesAll = (list, required) => required.every((id) => list.includes(id));

/**
 * Everything a screen needs to know about a circle's current question, from
 * the point of view of `userId`.
 *
 * phase:
 *   'none'     no question has been asked in this circle yet
 *   'open'     asked, answers hidden (still taking answers, however old)
 *   'revealed' answers showing
 */
export function pulseStatus(circle, userId, now = Date.now()) {
  const pulse = circle?.pulse || null;
  const members = circle?.members || [];
  const enoughPeople = members.length >= 2;

  if (!pulse || !pulse.questionId) {
    return {
      phase: 'none',
      question: null,
      enoughPeople,
      canStart: enoughPeople,
      nextStartAt: null,
    };
  }

  const question = getPulseQuestion(pulse.questionId);
  const askedTo = pulse.askedTo || [];
  const answeredBy = pulse.answeredBy || [];
  const startedAt = toMillis(pulse.startedAt) ?? now;
  const revealedAt = toMillis(pulse.revealedAt);
  const revealed = pulse.revealed === true;

  const revealDueAt = startedAt + PULSE_REVEAL_AFTER_DAYS * DAY;
  const everyoneAnswered = askedTo.length > 0 && includesAll(answeredBy, askedTo);
  const enoughAnswers = answeredBy.length >= PULSE_MIN_ANSWERS_TO_REVEAL;

  // The earliest a new question may be asked.
  const lifetimeEndsAt = startedAt + PULSE_LIFETIME_DAYS * DAY;
  const nextStartAt = revealed && revealedAt != null
    ? Math.min(revealedAt + PULSE_NEXT_AFTER_REVEAL_DAYS * DAY, lifetimeEndsAt)
    : lifetimeEndsAt;

  return {
    phase: revealed ? 'revealed' : 'open',
    question,
    pulseId: pulse.id,
    askedToMe: askedTo.includes(userId),
    iHaveAnswered: answeredBy.includes(userId),
    answeredCount: answeredBy.length,
    askedCount: askedTo.length,
    everyoneAnswered,
    revealDueAt,
    // Past the three days with fewer than two answers: it shows as soon as a
    // second person answers, and lapses when a new question is asked.
    waitingForSecondAnswer: !revealed && !enoughAnswers && now >= revealDueAt,
    // Reveal now: everyone asked has answered, or the three days are up -
    // and either way at least two answers are in.
    shouldReveal: !revealed && enoughAnswers && (everyoneAnswered || now >= revealDueAt),
    enoughPeople,
    canStart: enoughPeople && now > nextStartAt,
    nextStartAt,
  };
}

/**
 * Would `userId` answering now make everyone asked have answered (with at
 * least two answers)? If so the answer is written together with the reveal.
 */
export function answerCompletesPulse(pulse, userId) {
  const askedTo = pulse?.askedTo || [];
  const after = new Set([...(pulse?.answeredBy || []), userId]);
  return after.size >= PULSE_MIN_ANSWERS_TO_REVEAL && askedTo.every((id) => after.has(id));
}

/** A fresh question for a circle: the next one round, asked to everyone in it now. */
export function newPulseFor(circle, now = Date.now()) {
  return {
    id: `p${now.toString(36)}${Math.random().toString(36).slice(2, 8)}`,
    questionId: nextPulseQuestionId(circle?.pulse?.questionId, now),
    askedTo: [...(circle?.members || [])],
    answeredBy: [],
    revealed: false,
  };
}
